import { EnvelopeIcon, UserCircleIcon } from '@heroicons/react/20/solid';
import React, { Suspense, useRef, useContext, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../AuthContext';
import { getProfilePicSrc } from '../utils/mediaUrl';

// Lazy load the StaffNav and ManagerNav components
const LazyStaffNav = React.lazy(() => import('../staff/staffnav'));
const LazyManagerNav = React.lazy(() => import('../manager/managernav'));

// Dummy CameraIcon for illustration; replace with your actual icon import
const CameraIcon = (props) => (
  <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <circle cx="12" cy="13" r="3.5" />
    <path d="M9 3h6l1 2h4a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h4l1-2z" />
  </svg>
);

export default function MyProfile() {
  const location = useLocation();
  const person = location.state.person; // Get person passed via state
  const user = useContext(AuthContext); // Get user from AuthContext

  // Camera state
  const [showCamera, setShowCamera] = useState(false);
  const [cameraPerson, setCameraPerson] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const mypic = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/adminpic/${user.id}`, {
        method: "PUT",
        credentials: 'include',
        body: formData,
      });
      if (response.ok) {
        window.location.reload();
      } else {
        console.error("Failed to upload file", response.statusText);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };

  // Camera logic
  const openCamera = (person) => {
    setCameraPerson(person);
    setShowCamera(true);
    setTimeout(() => {
      if (videoRef.current) {
        navigator.mediaDevices.getUserMedia({ video: true })
          .then(stream => {
            videoRef.current.srcObject = stream;
            videoRef.current.play();
          })
          .catch(err => {
            alert("Could not access camera.");
            setShowCamera(false);
          });
      }
    }, 100);
  };

  const closeCamera = () => {
    setShowCamera(false);
    setCameraPerson(null);
    if (videoRef.current && videoRef.current.srcObject) {
      let tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
  };

  const takePicture = () => {
    if (videoRef.current && canvasRef.current) {
      const context = canvasRef.current.getContext('2d');
      context.drawImage(videoRef.current, 0, 0, 320, 240);
      canvasRef.current.toBlob(blob => {
        if (blob) {
          mypic(blob);
          closeCamera();
        }
      }, 'image/jpeg');
    }
  };

  const profilePic = getProfilePicSrc(person);

  return (
    <Suspense fallback={<div>Loading...</div>}>
      {user.access === 'manager' && user.access === 'admin' && (
        <LazyManagerNav />
      )}
      {user.access === 'staff' && (
        <LazyStaffNav />
      )}

      <div data-component="staff-profile-view">
        {/* Background Image */}
        <div>
          <img
            alt=""
            src="/1.png"
            className="h-32 w-full object-contain bg-blue-900 lg:h-48"
            loading="lazy"
          />
        </div>

        {/* Profile Information */}
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="-mt-12 sm:-mt-16 sm:flex sm:items-end sm:space-x-5">
            <div className="flex flex-col items-center">
              {/* Profile Picture or Initials */}
              {profilePic ? (
                <img
                  alt="Profile"
                  src={profilePic}
                  className="size-24 rounded-full ring-4 ring-white sm:size-32"
                  loading="lazy"
                />
              ) : (
                <div className="flex items-center justify-center w-12 h-12 bg-gray-200 text-gray-500 text-lg rounded-full">
                  {person.Name?.charAt(0)}
                </div>
              )}

              {/* Buttons under profile picture */}
              <div className="flex flex-col gap-2 mt-3 w-full items-center">
                <div className="relative w-full flex justify-center">
                  <button
                    type="button"
                    data-component="staff-profile-action"
                    data-feature="upload-photo"
                    className="rounded bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 relative"
                  >
                    Change
                    <input
                      type="file"
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      style={{ zIndex: 2 }}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          mypic(file);
                        }
                      }}
                    />
                  </button>
                </div>
                <button
                  type="button"
                  data-component="staff-profile-action"
                  data-feature="open-camera"
                  onClick={() => openCamera(person)}
                  className="rounded bg-yellow-500 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-yellow-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-600 flex items-center gap-1"
                >
                  <CameraIcon className="h-4 w-4" />
                  Take Picture
                </button>
              </div>
            </div>

            <div className="mt-6 sm:flex sm:min-w-0 sm:flex-1 sm:items-center sm:justify-end sm:space-x-6 sm:pb-1">
              <div className="mt-6 min-w-0 flex-1 sm:hidden md:block">
                <h1 className="truncate text-2xl font-bold text-gray-900">{person.Name}</h1>
              </div>
              <div className="mt-6 flex flex-col justify-stretch space-y-3 sm:flex-row sm:space-x-4 sm:space-y-0">
                {/* Message Button */}
                <button
                  type="button"
                  data-component="staff-profile-action"
                  data-feature="message"
                  className="inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  <EnvelopeIcon
                    aria-hidden="true"
                    className="-ml-0.5 mr-1.5 size-5 text-gray-400"
                  />
                  <span>Message</span>
                </button>

                {/* Location Button */}
                <button
                  type="button"
                  data-component="staff-profile-action"
                  data-feature="show-location"
                  className="inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  <UserCircleIcon
                    aria-hidden="true"
                    className="-ml-0.5 mr-1.5 size-5 text-gray-400"
                  />
                  <span>Location: {person.Location || 'N/A'}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Name for Mobile View */}
          <div className="mt-6 hidden min-w-0 flex-1 sm:block md:hidden">
            <h1 className="truncate text-2xl font-bold text-gray-900">{person.Name}</h1>
          </div>
        </div>

        {/* Camera Modal */}
        {showCamera && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
            <div className="bg-white p-4 rounded shadow-lg flex flex-col items-center">
              <video ref={videoRef} width={320} height={240} autoPlay className="mb-2" />
              <canvas ref={canvasRef} width={320} height={240} style={{ display: 'none' }} />
              <div className="flex gap-2 mt-2">
                <button
                  data-component="staff-profile-camera"
                  data-feature="capture"
                  onClick={takePicture}
                  className="rounded bg-green-600 px-3 py-1 text-xs font-semibold text-white hover:bg-green-700"
                >
                  Capture
                </button>
                <button
                  data-component="staff-profile-camera"
                  data-feature="close"
                  onClick={closeCamera}
                  className="rounded bg-gray-400 px-3 py-1 text-xs font-semibold text-white hover:bg-gray-500"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Suspense>
  );
}
